import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { createChart, ColorType, CandlestickSeries } from 'lightweight-charts';
import { 
  TrendingUp, TrendingDown, DollarSign, Bitcoin, LineChart, 
  ArrowUpRight, ArrowDownRight, Search, RefreshCw, Wallet,
  BookOpen, Play, Clock, Award, ChevronRight, Calendar, Check,
  ChevronDown, ChevronUp, PieChart, BarChart3, X, GraduationCap,
  Link2, Unlink, Key, Shield, Zap, ExternalLink
} from 'lucide-react';

import { API } from '@/config/api';

// Full course content with detailed lessons
const courses = [
  {
    id: 'crypto-basics',
    title: 'Crypto Fundamentals',
    description: 'Learn the basics of cryptocurrency, blockchain technology, and how to analyze crypto markets.',
    level: 'Beginner',
    image: 'https://images.pexels.com/photos/844124/pexels-photo-844124.jpeg',
    lessons: [
      { 
        id: 'c1', 
        title: 'What is Cryptocurrency?', 
        duration: '15 min read',
        content: `# What is Cryptocurrency?

Cryptocurrency is a digital or virtual form of currency that uses cryptography for security. Unlike traditional currencies issued by governments (fiat money), cryptocurrencies operate on decentralized networks based on blockchain technology.

## Key Characteristics

**1. Decentralization**
No single entity controls cryptocurrency. Instead, transactions are verified by a network of computers (nodes) around the world.

**2. Transparency**
All transactions are recorded on a public ledger (blockchain) that anyone can view.

**3. Security**
Cryptographic techniques make it extremely difficult to counterfeit or double-spend.

**4. Limited Supply**
Most cryptocurrencies have a fixed maximum supply, unlike fiat currencies that can be printed infinitely.

## Major Cryptocurrencies

- **Bitcoin (BTC)**: The first and most valuable cryptocurrency, created in 2009
- **Ethereum (ETH)**: A platform for smart contracts and decentralized applications
- **Litecoin (LTC)**: Often called "silver to Bitcoin's gold"
- **Ripple (XRP)**: Focused on fast, low-cost international transfers

## Why Cryptocurrency Matters

Cryptocurrency represents a fundamental shift in how we think about money and financial transactions. It enables:

- Peer-to-peer transactions without intermediaries
- Financial inclusion for the unbanked
- Programmable money through smart contracts
- New investment opportunities

## Getting Started

Before investing in cryptocurrency, make sure to:
1. Research thoroughly
2. Only invest what you can afford to lose
3. Use secure wallets
4. Understand the risks involved`
      },
      { 
        id: 'c2', 
        title: 'Understanding Blockchain', 
        duration: '20 min read',
        content: `# Understanding Blockchain Technology

Blockchain is the underlying technology that powers cryptocurrencies. Think of it as a digital ledger that records all transactions in a secure, transparent, and immutable way.

## How Blockchain Works

**1. Blocks**
Transactions are grouped together into "blocks." Each block contains:
- A list of transactions
- A timestamp
- A reference to the previous block (hash)
- A unique identifier (its own hash)

**2. Chain**
Blocks are linked together chronologically, forming a chain. This makes it nearly impossible to alter past transactions without changing all subsequent blocks.

**3. Consensus**
The network uses consensus mechanisms to validate new blocks:
- **Proof of Work (PoW)**: Miners solve complex puzzles
- **Proof of Stake (PoS)**: Validators stake their coins

## Why Blockchain is Revolutionary

**Immutability**: Once data is recorded, it cannot be changed
**Transparency**: Anyone can verify transactions
**Security**: Distributed nature makes hacking extremely difficult
**Efficiency**: Eliminates need for intermediaries

## Real-World Applications

Beyond cryptocurrency, blockchain is used in:
- Supply chain tracking
- Healthcare records
- Voting systems
- Real estate transactions
- Digital identity verification

## Key Takeaways

Understanding blockchain gives you a competitive advantage in the crypto market. It helps you:
- Evaluate new projects more effectively
- Understand why certain coins have value
- Make more informed investment decisions`
      },
      { 
        id: 'c3', 
        title: 'Setting Up Your First Wallet', 
        duration: '15 min read',
        content: `# Setting Up Your First Crypto Wallet

A cryptocurrency wallet is essential for storing, sending, and receiving digital assets. This guide will help you choose and set up your first wallet.

## Types of Wallets

### Hot Wallets (Online)
Connected to the internet, convenient but less secure.

- **Mobile Wallets**: Apps like Trust Wallet, Coinbase Wallet
- **Desktop Wallets**: Software installed on your computer
- **Web Wallets**: Accessed through a browser

### Cold Wallets (Offline)
Not connected to the internet, maximum security.

- **Hardware Wallets**: Physical devices like Ledger, Trezor
- **Paper Wallets**: Private keys printed on paper

## Setting Up a Hot Wallet (Example: Trust Wallet)

1. Download Trust Wallet from your app store
2. Create a new wallet
3. **IMPORTANT**: Write down your 12-24 word recovery phrase
4. Store the recovery phrase securely (never digitally!)
5. Verify your recovery phrase
6. Set up a PIN or biometric lock

## Security Best Practices

**Never share your private keys or recovery phrase**
Anyone with access to these can steal your funds.

**Use strong passwords**
Different password for each exchange/wallet.

**Enable 2FA**
Add an extra layer of security to all accounts.

**Start small**
Practice with small amounts before transferring large sums.

**Backup everything**
Store recovery phrases in multiple secure locations.

## Receiving Your First Crypto

1. Open your wallet
2. Select the cryptocurrency you want to receive
3. Copy your wallet address or show QR code
4. Share this address with the sender
5. Wait for network confirmation`
      },
      { 
        id: 'c4', 
        title: 'Reading Crypto Charts', 
        duration: '25 min read',
        content: `# Reading Cryptocurrency Charts

Technical analysis is essential for timing your trades. This lesson covers the basics of reading crypto charts.

## Candlestick Charts

Each candlestick shows price action for a specific time period:

**Body**: The thick part showing open and close prices
- Green/White: Price went UP (close > open)
- Red/Black: Price went DOWN (close < open)

**Wicks/Shadows**: Thin lines showing high and low prices

## Key Chart Patterns

### Support and Resistance
- **Support**: Price level where buying pressure prevents further decline
- **Resistance**: Price level where selling pressure prevents further rise

### Trend Lines
- **Uptrend**: Higher highs and higher lows
- **Downtrend**: Lower highs and lower lows
- **Sideways**: Price moving within a range

## Common Candlestick Patterns

**Bullish Patterns (Buy Signals)**
- Hammer: Long lower wick, small body at top
- Bullish Engulfing: Large green candle engulfs previous red
- Morning Star: Three-candle reversal pattern

**Bearish Patterns (Sell Signals)**
- Shooting Star: Long upper wick, small body at bottom
- Bearish Engulfing: Large red candle engulfs previous green
- Evening Star: Three-candle reversal pattern

## Volume Analysis

Volume confirms price movements:
- High volume on breakout = Strong signal
- Low volume on breakout = Potential false signal
- Volume should increase in trend direction

## Timeframes

Different timeframes for different strategies:
- **1m, 5m, 15m**: Day trading
- **1h, 4h**: Swing trading
- **1D, 1W**: Long-term investing

## Practice Tips

1. Start with higher timeframes (daily, weekly)
2. Look for confluence of multiple signals
3. Always consider the overall market trend
4. Use paper trading to practice
5. Keep a trading journal`
      },
    ]
  },
  {
    id: 'stock-trading',
    title: 'Stock Market Mastery',
    description: 'Master stock market analysis, reading charts, and building a diversified portfolio.',
    level: 'Intermediate',
    image: 'https://images.pexels.com/photos/534216/pexels-photo-534216.jpeg',
    lessons: [
      { 
        id: 's1', 
        title: 'Stock Market Basics', 
        duration: '20 min read',
        content: `# Stock Market Basics

The stock market is where buyers and sellers trade shares of publicly listed companies. Understanding how it works is the first step to becoming a successful investor.

## What is a Stock?

A stock (or share) represents partial ownership in a company. When you buy stock, you become a shareholder with rights to:
- Vote on company decisions
- Receive dividends (if distributed)
- Share in the company's profits

## How Stock Markets Work

### Exchanges
Stocks are traded on exchanges like:
- **NYSE**: New York Stock Exchange (largest by market cap)
- **NASDAQ**: Technology-heavy exchange
- **LSE**: London Stock Exchange

### Market Hours
- Pre-market: 4:00 AM - 9:30 AM ET
- Regular hours: 9:30 AM - 4:00 PM ET
- After-hours: 4:00 PM - 8:00 PM ET

## Types of Orders

**Market Order**: Buy/sell immediately at current price
**Limit Order**: Buy/sell only at your specified price or better
**Stop Order**: Triggers when price reaches a certain level
**Stop-Limit**: Combines stop and limit order features

## Key Market Indices

- **S&P 500**: 500 largest US companies
- **Dow Jones**: 30 major industrial companies
- **NASDAQ Composite**: All NASDAQ-listed stocks

## Market Participants

- **Retail Investors**: Individual investors like you
- **Institutional Investors**: Hedge funds, mutual funds, pension funds
- **Market Makers**: Provide liquidity by buying and selling

## Getting Started

1. Open a brokerage account
2. Fund your account
3. Research companies you're interested in
4. Start with a small investment
5. Learn and adjust your strategy`
      },
      { 
        id: 's2', 
        title: 'Fundamental Analysis', 
        duration: '30 min read',
        content: `# Fundamental Analysis

Fundamental analysis involves evaluating a company's financial health and intrinsic value to make investment decisions.

## Key Financial Statements

### Income Statement
Shows profitability over a period:
- Revenue (top line)
- Gross Profit
- Operating Income
- Net Income (bottom line)

### Balance Sheet
Shows financial position at a point in time:
- Assets (what the company owns)
- Liabilities (what it owes)
- Shareholders' Equity (net worth)

### Cash Flow Statement
Shows how cash moves through the business:
- Operating Cash Flow
- Investing Cash Flow
- Financing Cash Flow

## Important Ratios

### Valuation Ratios
**P/E Ratio** = Price / Earnings per Share
- Measures how much you pay for each dollar of earnings
- Lower P/E may indicate undervaluation

**P/B Ratio** = Price / Book Value
- Compares market value to book value
- Useful for asset-heavy companies

**P/S Ratio** = Price / Sales
- Useful for companies without profits yet

### Profitability Ratios
**ROE** = Net Income / Shareholders' Equity
- How efficiently company uses equity

**Profit Margin** = Net Income / Revenue
- How much profit per dollar of sales

### Financial Health Ratios
**Debt-to-Equity** = Total Debt / Shareholders' Equity
- Higher ratio = more leverage risk

**Current Ratio** = Current Assets / Current Liabilities
- Ability to pay short-term obligations

## Qualitative Factors

Don't forget to evaluate:
- Management quality
- Competitive advantages (moat)
- Industry trends
- Regulatory environment
- Brand strength`
      },
      { 
        id: 's3', 
        title: 'Technical Analysis Essentials', 
        duration: '35 min read',
        content: `# Technical Analysis Essentials

Technical analysis uses historical price and volume data to predict future price movements.

## Core Principles

1. **Price discounts everything**: All known information is reflected in price
2. **Prices move in trends**: Trends persist until reversed
3. **History repeats**: Patterns tend to recur

## Moving Averages

### Simple Moving Average (SMA)
Average price over a period:
- 20 SMA: Short-term trend
- 50 SMA: Medium-term trend
- 200 SMA: Long-term trend

### Exponential Moving Average (EMA)
Gives more weight to recent prices, more responsive to new information.

### Golden Cross & Death Cross
- **Golden Cross**: 50 SMA crosses above 200 SMA (bullish)
- **Death Cross**: 50 SMA crosses below 200 SMA (bearish)

## Key Indicators

### RSI (Relative Strength Index)
Measures momentum from 0-100:
- Above 70: Overbought (potential sell)
- Below 30: Oversold (potential buy)

### MACD (Moving Average Convergence Divergence)
Shows relationship between two moving averages:
- Signal line crossovers indicate buy/sell signals
- Histogram shows momentum strength

### Bollinger Bands
Three bands based on standard deviation:
- Upper band: Potential resistance
- Middle band: 20 SMA
- Lower band: Potential support
- Price touching bands can signal reversals

## Chart Patterns

### Reversal Patterns
- Head and Shoulders
- Double Top/Bottom
- Triple Top/Bottom

### Continuation Patterns
- Flags and Pennants
- Triangles (ascending, descending, symmetrical)
- Rectangles

## Volume Analysis

Volume confirms price movements:
- Increasing volume in uptrend = bullish
- Decreasing volume in uptrend = weakening
- Volume spikes often signal reversals`
      },
      { 
        id: 's4', 
        title: 'Building Your Portfolio', 
        duration: '25 min read',
        content: `# Building Your Investment Portfolio

A well-constructed portfolio balances risk and reward while aligning with your financial goals.

## Asset Allocation

### By Asset Class
- **Stocks**: Growth potential, higher risk
- **Bonds**: Stability, income generation
- **Cash**: Liquidity, safety
- **Alternatives**: Real estate, commodities, crypto

### Age-Based Rule
A simple guideline: 110 - your age = stock allocation %
- Age 30: 80% stocks, 20% bonds
- Age 50: 60% stocks, 40% bonds
- Age 70: 40% stocks, 60% bonds

## Diversification

### By Sector
Spread investments across different industries:
- Technology
- Healthcare
- Financial
- Consumer
- Energy
- Industrial

### By Geography
- Domestic stocks
- International developed markets
- Emerging markets

### By Company Size
- Large-cap (stable, slower growth)
- Mid-cap (balance of growth and stability)
- Small-cap (higher growth potential, more volatile)

## Investment Strategies

### Dollar-Cost Averaging
Invest fixed amounts regularly regardless of price:
- Reduces impact of volatility
- Removes emotion from investing
- Works well for long-term investors

### Value Investing
Buy undervalued companies with strong fundamentals:
- Look for low P/E, P/B ratios
- Focus on intrinsic value
- Requires patience

### Growth Investing
Buy companies with high growth potential:
- Higher P/E acceptable for fast growers
- Focus on revenue growth, market share
- More volatile

## Risk Management

**Never invest more than you can afford to lose**

**Position sizing**: Limit each position to 5-10% of portfolio
**Stop losses**: Set automatic sell points to limit losses
**Rebalancing**: Periodically adjust back to target allocation`
      },
    ]
  },
  {
    id: 'forex-trading',
    title: 'Forex Trading Essentials',
    description: 'Understand currency pairs, pip values, and forex trading strategies.',
    level: 'Intermediate',
    image: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg',
    lessons: [
      { 
        id: 'f1', 
        title: 'Introduction to Forex', 
        duration: '20 min read',
        content: `# Introduction to Forex Trading

The foreign exchange (forex) market is the world's largest financial market, with over $6 trillion traded daily.

## What is Forex?

Forex trading involves exchanging one currency for another. Currencies are always traded in pairs:
- EUR/USD (Euro vs US Dollar)
- GBP/USD (British Pound vs US Dollar)
- USD/JPY (US Dollar vs Japanese Yen)

## Major Currency Pairs

### The Majors
Most traded pairs, all include USD:
- EUR/USD (Euro)
- GBP/USD (British Pound)
- USD/JPY (Japanese Yen)
- USD/CHF (Swiss Franc)
- AUD/USD (Australian Dollar)
- USD/CAD (Canadian Dollar)

### Crosses
Major currencies without USD:
- EUR/GBP, EUR/JPY, GBP/JPY

### Exotics
Major currency + emerging market currency:
- USD/TRY, EUR/ZAR, GBP/MXN

## Market Sessions

The forex market operates 24/5 across global sessions:

**Sydney Session**: 5pm - 2am EST
**Tokyo Session**: 7pm - 4am EST
**London Session**: 3am - 12pm EST (highest volume)
**New York Session**: 8am - 5pm EST

Best trading times are during session overlaps.

## Why Trade Forex?

- **Liquidity**: Easy to enter and exit positions
- **Leverage**: Control large positions with small capital
- **24-hour market**: Trade anytime during the week
- **Low costs**: Tight spreads, no commissions with many brokers
- **Profit in any direction**: Go long or short easily

## Risks

- **Leverage risk**: Amplifies losses as well as gains
- **Volatility**: Currency prices can move quickly
- **Complexity**: Many factors affect exchange rates`
      },
      { 
        id: 'f2', 
        title: 'Pips, Lots, and Leverage', 
        duration: '25 min read',
        content: `# Pips, Lots, and Leverage

Understanding these core concepts is essential before placing any forex trade.

## What is a Pip?

A pip (percentage in point) is the smallest price move in forex:
- For most pairs: 0.0001 (4th decimal place)
- For JPY pairs: 0.01 (2nd decimal place)

**Example**: EUR/USD moves from 1.1050 to 1.1051 = 1 pip

### Pipettes
Some brokers show 5 decimal places. The 5th decimal is a pipette (1/10 of a pip).

## Lot Sizes

**Standard Lot**: 100,000 units
- 1 pip = $10 for USD pairs

**Mini Lot**: 10,000 units
- 1 pip = $1 for USD pairs

**Micro Lot**: 1,000 units
- 1 pip = $0.10 for USD pairs

**Nano Lot**: 100 units
- 1 pip = $0.01 for USD pairs

## Calculating Pip Value

**Formula**: Pip Value = (1 pip / exchange rate) × lot size

**Example** (EUR/USD at 1.1050, 1 standard lot):
Pip Value = (0.0001 / 1.1050) × 100,000 = $9.05

## Leverage

Leverage allows you to control large positions with less capital.

**Common Leverage Ratios**:
- 50:1 = $1 controls $50
- 100:1 = $1 controls $100
- 500:1 = $1 controls $500

### Margin
The amount required to open a position.

**Example**: 100:1 leverage
To control $100,000 (1 lot), you need $1,000 margin.

### Margin Call
When your account equity falls below required margin, the broker may close your positions.

## Risk Management

**The 1% Rule**: Never risk more than 1-2% of your account on a single trade.

**Example**: $10,000 account
- Max risk per trade: $100-$200
- If stop loss is 20 pips on a mini lot ($2/pip)
- Loss = 20 × $2 = $40 (0.4% - within limits)

**Always use stop losses!**`
      },
      { 
        id: 'f3', 
        title: 'Forex Trading Strategies', 
        duration: '30 min read',
        content: `# Forex Trading Strategies

Different strategies suit different trading styles and time commitments.

## Scalping

**Timeframe**: Seconds to minutes
**Goal**: Many small profits

**Characteristics**:
- Hold positions for very short periods
- Target 5-10 pips per trade
- Requires intense focus and quick execution
- High win rate needed due to spread costs

**Best For**: Full-time traders with fast execution

## Day Trading

**Timeframe**: Minutes to hours
**Goal**: Capture intraday moves

**Characteristics**:
- Close all positions before market close
- Avoid overnight risk
- Trade major sessions for volatility
- Focus on 15m, 1h charts

**Popular Setups**:
- Breakout trading
- Range trading
- News trading

## Swing Trading

**Timeframe**: Days to weeks
**Goal**: Capture larger market moves

**Characteristics**:
- Hold positions overnight
- Less screen time required
- Focus on 4h, daily charts
- Let profits run with trailing stops

**Best For**: Part-time traders with day jobs

## Position Trading

**Timeframe**: Weeks to months
**Goal**: Capture major trends

**Characteristics**:
- Based on fundamental analysis
- Weekly and monthly charts
- Low trading frequency
- Large stop losses required

## Common Strategies

### Trend Following
- Trade in direction of the trend
- "The trend is your friend"
- Use moving averages to identify trend

### Breakout Trading
- Enter when price breaks key levels
- Wait for confirmation
- Volume should increase on breakout

### Range Trading
- Buy at support, sell at resistance
- Works in consolidating markets
- Use oscillators like RSI

### Carry Trade
- Borrow low-interest currency
- Invest in high-interest currency
- Profit from interest rate differential`
      },
      { 
        id: 'f4', 
        title: 'Risk Management in Forex', 
        duration: '20 min read',
        content: `# Risk Management in Forex

Risk management is the most important skill in trading. Without it, even the best strategy will fail.

## The Golden Rules

**1. Never risk more than 1-2% per trade**
This ensures you can survive losing streaks.

**2. Always use stop losses**
Define your exit before entering.

**3. Maintain proper risk-reward ratio**
Aim for at least 1:2 (risk $1 to make $2).

**4. Don't over-leverage**
Higher leverage = higher risk of account wipeout.

## Position Sizing

**Calculate before every trade:**

1. Determine account risk (1-2%)
2. Identify stop loss in pips
3. Calculate position size

**Formula**:
Position Size = Account Risk / (Stop Loss Pips × Pip Value)

**Example**:
- Account: $10,000
- Risk: 2% = $200
- Stop Loss: 50 pips
- Pip value (mini lot): $1

Position Size = $200 / (50 × $1) = 4 mini lots

## Stop Loss Placement

**Technical Stops**:
- Below support for longs
- Above resistance for shorts
- Beyond recent swing high/low

**Volatility Stops**:
- Use ATR (Average True Range)
- Stop = Entry ± (ATR × multiplier)

**Never move stops further away**
Only move stops to lock in profits.

## Take Profit Strategies

**Fixed Target**:
- Set R:R ratio (e.g., 1:2, 1:3)
- Place TP at next support/resistance

**Trailing Stop**:
- Move stop to breakeven once in profit
- Trail stop as price moves in your favor

**Partial Profits**:
- Close 50% at first target
- Let rest run with trailing stop

## Trading Psychology

- **Accept losses as part of trading**
- **Don't revenge trade** after losses
- **Stick to your plan**
- **Keep a trading journal**
- **Take breaks** when emotional`
      },
    ]
  },
  {
    id: 'technical-analysis',
    title: 'Technical Analysis Deep Dive',
    description: 'Learn candlestick patterns, indicators, and chart analysis techniques.',
    level: 'Advanced',
    image: 'https://images.pexels.com/photos/7567486/pexels-photo-7567486.jpeg',
    lessons: [
      { 
        id: 't1', 
        title: 'Candlestick Patterns', 
        duration: '30 min read',
        content: `# Candlestick Patterns

Japanese candlestick patterns have been used for centuries to predict price movements.

## Single Candle Patterns

### Doji
- Open and close are nearly equal
- Shows market indecision
- Potential reversal when appearing after a trend

**Types**:
- Standard Doji: Cross shape
- Long-legged Doji: Long wicks
- Gravestone Doji: Long upper wick (bearish)
- Dragonfly Doji: Long lower wick (bullish)

### Hammer & Hanging Man
- Small body at top, long lower wick
- **Hammer** (at bottom): Bullish reversal
- **Hanging Man** (at top): Bearish reversal

### Shooting Star & Inverted Hammer
- Small body at bottom, long upper wick
- **Shooting Star** (at top): Bearish reversal
- **Inverted Hammer** (at bottom): Bullish reversal

## Double Candle Patterns

### Engulfing Patterns
**Bullish Engulfing**: Large green candle completely engulfs previous red candle
**Bearish Engulfing**: Large red candle completely engulfs previous green candle

### Tweezer Tops/Bottoms
Two candles with matching highs (top) or lows (bottom):
- Tweezer Top: Bearish reversal
- Tweezer Bottom: Bullish reversal

## Triple Candle Patterns

### Morning Star (Bullish)
1. Large red candle
2. Small candle (gap down)
3. Large green candle closing above midpoint of #1

### Evening Star (Bearish)
1. Large green candle
2. Small candle (gap up)
3. Large red candle closing below midpoint of #1

### Three White Soldiers (Bullish)
Three consecutive green candles, each closing higher

### Three Black Crows (Bearish)
Three consecutive red candles, each closing lower

## Using Candlestick Patterns

1. **Context matters**: Patterns are more reliable at key levels
2. **Confirm with volume**: Higher volume = stronger signal
3. **Wait for confirmation**: Don't trade on pattern alone
4. **Use with other analysis**: Combine with indicators and levels`
      },
      { 
        id: 't2', 
        title: 'Chart Patterns', 
        duration: '35 min read',
        content: `# Chart Patterns

Chart patterns help identify potential breakouts and reversals.

## Reversal Patterns

### Head and Shoulders
**Structure**:
- Left shoulder: High, pullback
- Head: Higher high, pullback to neckline
- Right shoulder: Lower high

**Trading**:
- Entry: Break below neckline
- Target: Distance from head to neckline
- Stop: Above right shoulder

### Inverse Head and Shoulders
Mirror image of H&S, signals bullish reversal.

### Double Top
- Two peaks at similar level
- "M" shape
- Bearish reversal

### Double Bottom
- Two troughs at similar level
- "W" shape
- Bullish reversal

### Triple Top/Bottom
Three peaks or troughs at similar levels. Stronger signal than double patterns.

## Continuation Patterns

### Flags
- Sharp move (flagpole)
- Tight consolidation against trend
- Continuation in original direction

### Pennants
- Similar to flags but triangular consolidation
- Forms after sharp move
- Quick resolution expected

### Triangles

**Ascending Triangle**:
- Flat resistance, rising support
- Usually bullish (breaks up)

**Descending Triangle**:
- Flat support, falling resistance
- Usually bearish (breaks down)

**Symmetrical Triangle**:
- Converging trendlines
- Can break either direction
- Trade the breakout

### Rectangles
- Price bounces between horizontal support and resistance
- Trade the breakout or range

## Measuring Price Targets

**For most patterns**:
Target = Breakout point ± pattern height

**Example** (Head and Shoulders):
- Head at $50, Neckline at $45
- Pattern height = $5
- Target = $45 - $5 = $40

## Pattern Failure

Patterns don't always work. When patterns fail:
- Can signal strong move in opposite direction
- Always use stop losses
- Failed patterns often become other patterns`
      },
      { 
        id: 't3', 
        title: 'Advanced Indicators', 
        duration: '40 min read',
        content: `# Advanced Technical Indicators

Beyond basic indicators, these tools can enhance your analysis.

## Fibonacci Retracements

Based on the Fibonacci sequence, these levels often act as support/resistance.

**Key Levels**:
- 23.6%
- 38.2%
- 50% (not Fibonacci but commonly used)
- 61.8% (Golden Ratio)
- 78.6%

**Using Fibonacci**:
1. Identify a significant swing high and low
2. Draw Fibonacci from low to high (uptrend) or high to low (downtrend)
3. Watch for reactions at key levels
4. Combine with other signals for entries

## Bollinger Bands

Three bands based on standard deviation:

**Structure**:
- Middle Band: 20-period SMA
- Upper Band: Middle + (2 × standard deviation)
- Lower Band: Middle - (2 × standard deviation)

**Interpretation**:
- Price touching upper band: Potentially overbought
- Price touching lower band: Potentially oversold
- Band squeeze: Low volatility, breakout expected
- Band expansion: High volatility

**Strategies**:
- Mean reversion: Trade bounces from bands
- Breakout: Trade when bands expand after squeeze

## Volume Analysis

### On-Balance Volume (OBV)
Cumulative indicator adding/subtracting volume based on price direction.
- Rising OBV: Accumulation
- Falling OBV: Distribution
- Divergence with price signals potential reversal

### Volume Profile
Shows volume at each price level:
- High volume nodes: Strong support/resistance
- Low volume nodes: Price moves quickly through
- Value Area: Where 70% of volume occurred

## Ichimoku Cloud

Japanese system providing:
- Trend direction
- Support/resistance
- Momentum

**Components**:
- Tenkan-sen: 9-period midpoint
- Kijun-sen: 26-period midpoint
- Senkou Span A: Average of above, plotted 26 periods ahead
- Senkou Span B: 52-period midpoint, plotted 26 periods ahead
- Chikou Span: Close plotted 26 periods back

**Cloud Interpretation**:
- Price above cloud: Bullish
- Price below cloud: Bearish
- Thick cloud: Strong support/resistance
- Cloud crossover: Trend change

## Average True Range (ATR)

Measures volatility:
- Higher ATR: More volatility
- Lower ATR: Less volatility

**Uses**:
- Position sizing
- Stop loss placement
- Identifying breakouts`
      },
      { 
        id: 't4', 
        title: 'Putting It All Together', 
        duration: '35 min read',
        content: `# Creating Your Trading System

A complete trading system combines everything you've learned into a repeatable process.

## System Components

### 1. Market Selection
Decide what you'll trade:
- Specific pairs/stocks
- Market conditions you'll trade
- Sessions you'll be active

### 2. Setup Identification
What conditions must be met?
- Trend direction (MA alignment)
- Price at key level (S/R, Fibonacci)
- Pattern formation
- Indicator confirmation

### 3. Entry Triggers
Specific signal to enter:
- Candlestick pattern
- Indicator crossover
- Break of level
- Price action

### 4. Position Sizing
How much to risk:
- Fixed percentage of account
- Based on stop loss distance
- ATR-based sizing

### 5. Stop Loss
Where to exit if wrong:
- Below/above key level
- ATR-based distance
- Pattern invalidation

### 6. Take Profit
When to take profits:
- Fixed R:R ratio
- Next S/R level
- Trailing stop
- Partial profits

## Example System

**Setup**: Daily uptrend (price above 200 SMA)
**Entry**: Pullback to 50 SMA + bullish engulfing
**Stop**: Below swing low or 1.5 × ATR
**Target**: 2× risk (1:2 R:R)

## Trading Plan Checklist

Before each trade, answer:
- [ ] Is this my setup?
- [ ] Does the trend support my direction?
- [ ] Where is my stop loss?
- [ ] Where is my take profit?
- [ ] What's my position size?
- [ ] Am I following my rules?

## Backtesting

Before trading live:
1. Test system on historical data
2. Record at least 100 trades
3. Calculate win rate and R:R
4. Ensure positive expectancy
5. Forward test on demo account

## Trading Journal

Record every trade:
- Entry and exit prices
- Setup identified
- Emotions during trade
- What went right/wrong
- Screenshots

Review weekly to improve.

## Continuous Improvement

- Analyze losing trades
- Identify recurring mistakes
- Refine your rules
- Stay disciplined
- Keep learning

**Remember**: Consistency beats perfection. A simple system followed religiously will outperform a complex system applied randomly.`
      },
    ]
  }
];

export default function TradingHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('trading');
  const [marketTab, setMarketTab] = useState('crypto');
  const [cryptoData, setCryptoData] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioAnalytics, setPortfolioAnalytics] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  
  // Course state
  const [courseProgress, setCourseProgress] = useState({});
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonModalOpen, setLessonModalOpen] = useState(false);
  
  // Trade form
  const [tradeForm, setTradeForm] = useState({
    side: 'buy',
    quantity: '',
    symbol: '',
    price: 0
  });
  
  // Stock search
  const [stockSearch, setStockSearch] = useState('');
  const [stockResults, setStockResults] = useState([]);
  const [stockQuote, setStockQuote] = useState(null);
  
  // Forex
  const [forexFrom, setForexFrom] = useState('USD');
  const [forexTo, setForexTo] = useState('EUR');
  const [forexRate, setForexRate] = useState(null);
  
  // Alpaca broker state
  const [alpacaStatus, setAlpacaStatus] = useState({ available: false, connected: false });
  const [alpacaAccount, setAlpacaAccount] = useState(null);
  const [alpacaPositions, setAlpacaPositions] = useState([]);
  const [alpacaOrders, setAlpacaOrders] = useState([]);
  const [brokerModalOpen, setBrokerModalOpen] = useState(false);
  const [alpacaCredentials, setAlpacaCredentials] = useState({ api_key: '', secret_key: '', paper: true });
  const [connectingBroker, setConnectingBroker] = useState(false);
  const [tradingMode, setTradingMode] = useState('paper'); // 'paper' or 'live'
  
  useEffect(() => {
    fetchCryptoData();
    fetchPortfolio();
    fetchTrades();
    fetchCourseProgress();
    fetchAlpacaStatus();
  }, []);
  
  // Fetch Alpaca status
  const fetchAlpacaStatus = async () => {
    try {
      const response = await fetch(`${API}/alpaca/status`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAlpacaStatus(data);
        if (data.connected) {
          fetchAlpacaAccount();
          fetchAlpacaPositions();
          fetchAlpacaOrders();
        }
      }
    } catch (error) {
      console.error('Alpaca status error:', error);
    }
  };
  
  const fetchAlpacaAccount = async () => {
    try {
      const response = await fetch(`${API}/alpaca/account`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAlpacaAccount(data);
        setTradingMode(data.paper ? 'paper' : 'live');
      }
    } catch (error) {
      console.error('Alpaca account error:', error);
    }
  };
  
  const fetchAlpacaPositions = async () => {
    try {
      const response = await fetch(`${API}/alpaca/positions`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAlpacaPositions(data);
      }
    } catch (error) {
      console.error('Alpaca positions error:', error);
    }
  };
  
  const fetchAlpacaOrders = async () => {
    try {
      const response = await fetch(`${API}/alpaca/orders?status=all`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAlpacaOrders(data);
      }
    } catch (error) {
      console.error('Alpaca orders error:', error);
    }
  };
  
  const connectAlpaca = async () => {
    if (!alpacaCredentials.api_key || !alpacaCredentials.secret_key) {
      toast.error('Please enter your API credentials');
      return;
    }
    
    setConnectingBroker(true);
    try {
      const response = await fetch(`${API}/alpaca/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(alpacaCredentials)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Connected to Alpaca! ${data.paper ? '(Paper Trading)' : '(Live Trading)'}`);
        setBrokerModalOpen(false);
        setAlpacaCredentials({ api_key: '', secret_key: '', paper: true });
        fetchAlpacaStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to connect');
      }
    } catch (error) {
      toast.error('Connection failed');
    } finally {
      setConnectingBroker(false);
    }
  };
  
  const disconnectAlpaca = async () => {
    try {
      const response = await fetch(`${API}/alpaca/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Disconnected from Alpaca');
        setAlpacaStatus({ available: true, connected: false });
        setAlpacaAccount(null);
        setAlpacaPositions([]);
        setAlpacaOrders([]);
      }
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };
  
  const placeAlpacaOrder = async () => {
    if (!tradeForm.quantity || !tradeForm.symbol) {
      toast.error('Please fill in all trade details');
      return;
    }
    
    try {
      const response = await fetch(`${API}/alpaca/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          symbol: tradeForm.symbol.toUpperCase(),
          qty: parseFloat(tradeForm.quantity),
          side: tradeForm.side,
          order_type: 'market',
          time_in_force: 'day'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Order placed: ${data.side} ${data.qty} ${data.symbol}`);
        fetchAlpacaAccount();
        fetchAlpacaPositions();
        fetchAlpacaOrders();
        setTradeForm(prev => ({ ...prev, quantity: '' }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Order failed');
      }
    } catch (error) {
      toast.error('Order execution failed');
    }
  };
  
  const cancelAlpacaOrder = async (orderId) => {
    try {
      const response = await fetch(`${API}/alpaca/orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Order cancelled');
        fetchAlpacaOrders();
      }
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };
  
  // Initialize chart
  useEffect(() => {
    if (chartContainerRef.current && selectedAsset && activeTab === 'trading') {
      if (chartRef.current) {
        chartRef.current.remove();
      }
      
      const chart = createChart(chartContainerRef.current, {
        layout: {
          background: { type: ColorType.Solid, color: '#1c1814' },
          textColor: '#FAFAFA',
        },
        grid: {
          vertLines: { color: 'rgba(212, 175, 55, 0.1)' },
          horzLines: { color: 'rgba(212, 175, 55, 0.1)' },
        },
        width: chartContainerRef.current.clientWidth,
        height: 400,
        crosshair: {
          mode: 1,
          vertLine: { color: '#D4AF37', width: 1, style: 2 },
          horzLine: { color: '#D4AF37', width: 1, style: 2 },
        },
        localization: {
          locale: 'en-US',
        },
        timeScale: {
          borderColor: 'rgba(212, 175, 55, 0.3)',
          timeVisible: true,
        },
        rightPriceScale: {
          borderColor: 'rgba(212, 175, 55, 0.3)',
        },
      });
      
      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#10B981',
        downColor: '#EF4444',
        borderUpColor: '#10B981',
        borderDownColor: '#EF4444',
        wickUpColor: '#10B981',
        wickDownColor: '#EF4444',
      });
      
      const sparkline = selectedAsset.sparkline_in_7d?.price || [];
      const now = Date.now();
      const candleData = [];
      
      for (let i = 0; i < Math.min(sparkline.length - 1, 168); i++) {
        const time = Math.floor((now - (168 - i) * 3600000) / 1000);
        const open = sparkline[i];
        const close = sparkline[i + 1] || open;
        const high = Math.max(open, close) * (1 + Math.random() * 0.02);
        const low = Math.min(open, close) * (1 - Math.random() * 0.02);
        candleData.push({ time, open, high, low, close });
      }
      
      candlestickSeries.setData(candleData);
      chart.timeScale().fitContent();
      chartRef.current = chart;
      
      const handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          try {
            chart.applyOptions({ width: chartContainerRef.current.clientWidth });
          } catch (e) {
            // Chart may have been disposed
          }
        }
      };
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          try {
            chartRef.current.remove();
          } catch (e) {
            // Chart may already be disposed
          }
          chartRef.current = null;
        }
      };
    }
  }, [selectedAsset, activeTab]);
  
  const fetchCryptoData = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(`${API}/market/crypto`);
      if (response.ok) {
        const data = await response.json();
        setCryptoData(Array.isArray(data) ? data : []);
        if (data.length > 0 && !selectedAsset) {
          setSelectedAsset(data[0]);
          setTradeForm(prev => ({ ...prev, symbol: data[0].symbol.toUpperCase(), price: data[0].current_price }));
        }
      }
    } catch (error) {
      console.error('Crypto fetch error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const fetchPortfolio = async () => {
    try {
      const response = await fetch(`${API}/portfolio`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setPortfolio(data[0]);
          fetchPortfolioAnalytics(data[0].portfolio_id);
        }
      }
    } catch (error) {
      console.error('Portfolio fetch error:', error);
    }
  };
  
  const fetchPortfolioAnalytics = async (portfolioId) => {
    try {
      const response = await fetch(`${API}/portfolio/${portfolioId}/analytics`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPortfolioAnalytics(data);
      }
    } catch (error) {
      console.error('Analytics fetch error:', error);
    }
  };
  
  const fetchTrades = async () => {
    try {
      const response = await fetch(`${API}/trades`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTrades(data);
      }
    } catch (error) {
      console.error('Trades fetch error:', error);
    }
  };
  
  const fetchCourseProgress = async () => {
    try {
      const response = await fetch(`${API}/courses/progress`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const progressMap = {};
        data.forEach(p => {
          progressMap[p.course_id] = p.completed_lessons || [];
        });
        setCourseProgress(progressMap);
      }
    } catch (error) {
      console.error('Course progress fetch error:', error);
    }
  };
  
  const toggleLessonComplete = async (courseId, lessonId) => {
    const isCompleted = (courseProgress[courseId] || []).includes(lessonId);
    const endpoint = isCompleted ? '/courses/uncomplete-lesson' : '/courses/complete-lesson';
    
    try {
      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ course_id: courseId, lesson_id: lessonId })
      });
      
      if (response.ok) {
        setCourseProgress(prev => {
          const current = prev[courseId] || [];
          if (isCompleted) {
            return { ...prev, [courseId]: current.filter(id => id !== lessonId) };
          } else {
            return { ...prev, [courseId]: [...current, lessonId] };
          }
        });
        toast.success(isCompleted ? 'Lesson unmarked' : 'Lesson completed! 🎉');
      }
    } catch (error) {
      console.error('Toggle lesson error:', error);
      toast.error('Failed to update progress');
    }
  };
  
  const searchStocks = async () => {
    if (!stockSearch.trim()) return;
    try {
      const response = await fetch(`${API}/market/stocks/search/${stockSearch}`);
      if (response.ok) {
        const data = await response.json();
        setStockResults(data);
      }
    } catch (error) {
      console.error('Stock search error:', error);
    }
  };
  
  const getStockQuote = async (symbol) => {
    try {
      const response = await fetch(`${API}/market/stocks/${symbol}`);
      if (response.ok) {
        const data = await response.json();
        if (!data.error) {
          setStockQuote(data);
          setSelectedAsset({ symbol, ...data, type: 'stock' });
          setTradeForm(prev => ({ ...prev, symbol, price: data.price }));
        }
      }
    } catch (error) {
      console.error('Stock quote error:', error);
    }
  };
  
  const getForexRate = async () => {
    try {
      const response = await fetch(`${API}/market/forex/${forexFrom}/${forexTo}`);
      if (response.ok) {
        const data = await response.json();
        if (!data.error) setForexRate(data);
      }
    } catch (error) {
      console.error('Forex error:', error);
    }
  };
  
  const executeTrade = async () => {
    if (!tradeForm.quantity || !tradeForm.symbol || !portfolio) {
      toast.error('Please fill in all trade details');
      return;
    }
    
    try {
      const response = await fetch(`${API}/trade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          portfolio_id: portfolio.portfolio_id,
          symbol: tradeForm.symbol.toUpperCase(),
          asset_type: marketTab,
          side: tradeForm.side,
          quantity: parseFloat(tradeForm.quantity),
          price: tradeForm.price,
          is_paper: true
        })
      });
      
      if (response.ok) {
        toast.success(`${tradeForm.side === 'buy' ? 'Bought' : 'Sold'} ${tradeForm.quantity} ${tradeForm.symbol}`);
        fetchPortfolio();
        fetchTrades();
        setTradeForm(prev => ({ ...prev, quantity: '' }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Trade failed');
      }
    } catch (error) {
      console.error('Trade error:', error);
      toast.error('Trade execution failed');
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };
  
  const formatPercent = (value) => {
    const num = parseFloat(value) || 0;
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
  };
  
  const getCourseCompletionPercent = (courseId, lessons) => {
    const completed = (courseProgress[courseId] || []).length;
    return Math.round((completed / lessons.length) * 100);
  };
  
  const getTotalProgress = () => {
    let totalLessons = 0;
    let completedLessons = 0;
    courses.forEach(course => {
      totalLessons += course.lessons.length;
      completedLessons += (courseProgress[course.id] || []).length;
    });
    return totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  };
  
  const openLesson = (course, lesson) => {
    setSelectedLesson({ course, lesson });
    setLessonModalOpen(true);
  };
  
  return (
    <div className="min-h-screen bg-[#1c1814] pt-24 pb-12" data-testid="trading-hub">
      {/* Header */}
      <div className="border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-4">
                <h1 className="font-display text-3xl sm:text-4xl text-[#FAFAFA]">
                  Trading <span className="text-[#D4AF37]">Platform</span>
                </h1>
                {alpacaStatus.connected && (
                  <Badge className={`${alpacaAccount?.paper ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'} border-0`}>
                    <Zap className="h-3 w-3 mr-1" />
                    {alpacaAccount?.paper ? 'Paper Trading' : 'Live Trading'}
                  </Badge>
                )}
              </div>
              <p className="font-body text-[#FAFAFA]/60 mt-1">
                {alpacaStatus.connected 
                  ? `Connected to Alpaca • Buying Power: ${formatCurrency(alpacaAccount?.buying_power || 0)}`
                  : 'Learn, practice, and master the markets'
                }
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Broker Connection Button */}
              {alpacaStatus.connected ? (
                <Button 
                  variant="outline"
                  onClick={disconnectAlpaca}
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                  data-testid="disconnect-broker-btn"
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  Connected
                </Button>
              ) : (
                <Button 
                  onClick={() => setBrokerModalOpen(true)}
                  className="bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90"
                  data-testid="connect-broker-btn"
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  Connect Broker
                </Button>
              )}
              
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-[#252540] border border-[#D4AF37]/20">
                  <TabsTrigger 
                    value="trading"
                    className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814]"
                    data-testid="tab-trading"
                  >
                    <LineChart className="h-4 w-4 mr-2" />
                    Trading
                  </TabsTrigger>
                  <TabsTrigger 
                    value="courses"
                    className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814]"
                    data-testid="tab-courses"
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Courses
                  </TabsTrigger>
                  <TabsTrigger 
                    value="portfolio"
                    className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814]"
                    data-testid="tab-portfolio"
                  >
                    <PieChart className="h-4 w-4 mr-2" />
                    Portfolio
                  </TabsTrigger>
                  <TabsTrigger 
                    value="history"
                    className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814]"
                    data-testid="tab-history"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    History
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trading Tab */}
        {activeTab === 'trading' && (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Left - Market List */}
            <div className="lg:col-span-1">
              <Card className="card-finance" data-testid="market-list">
                <CardHeader className="border-b border-[#D4AF37]/20 pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-display text-lg text-[#FAFAFA]">Markets</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={fetchCryptoData}
                      disabled={refreshing}
                      className="text-[#D4AF37]"
                    >
                      <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  <Tabs value={marketTab} onValueChange={setMarketTab} className="mt-4">
                    <TabsList className="bg-[#1c1814] w-full grid grid-cols-3">
                      <TabsTrigger value="crypto" className="text-xs data-[state=active]:bg-[#D4AF37]/20 data-[state=active]:text-[#D4AF37]">
                        Crypto
                      </TabsTrigger>
                      <TabsTrigger value="stocks" className="text-xs data-[state=active]:bg-[#D4AF37]/20 data-[state=active]:text-[#D4AF37]">
                        Stocks
                      </TabsTrigger>
                      <TabsTrigger value="forex" className="text-xs data-[state=active]:bg-[#D4AF37]/20 data-[state=active]:text-[#D4AF37]">
                        Forex
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    {marketTab === 'crypto' && (
                      <div className="divide-y divide-[#D4AF37]/10">
                        {cryptoData.slice(0, 15).map((coin) => (
                          <button
                            key={coin.id}
                            onClick={() => {
                              setSelectedAsset(coin);
                              setTradeForm(prev => ({ ...prev, symbol: coin.symbol.toUpperCase(), price: coin.current_price }));
                            }}
                            className={`w-full p-3 flex items-center justify-between hover:bg-[#D4AF37]/10 transition-colors ${
                              selectedAsset?.id === coin.id ? 'bg-[#D4AF37]/10 border-l-2 border-[#D4AF37]' : ''
                            }`}
                            data-testid={`crypto-${coin.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <img src={coin.image} alt={coin.name} className="w-6 h-6" />
                              <div className="text-left">
                                <p className="font-body text-sm text-[#FAFAFA]">{coin.symbol.toUpperCase()}</p>
                                <p className="text-xs text-[#FAFAFA]/50">{coin.name}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-body text-sm text-[#FAFAFA]">{formatCurrency(coin.current_price)}</p>
                              <p className={`text-xs ${coin.price_change_percentage_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {formatPercent(coin.price_change_percentage_24h)}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {marketTab === 'stocks' && (
                      <div className="p-4">
                        <div className="flex gap-2 mb-4">
                          <Input
                            value={stockSearch}
                            onChange={(e) => setStockSearch(e.target.value)}
                            placeholder="Search stocks..."
                            className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA] text-sm"
                            onKeyPress={(e) => e.key === 'Enter' && searchStocks()}
                          />
                          <Button onClick={searchStocks} size="icon" className="bg-[#D4AF37] text-[#1c1814]">
                            <Search className="h-4 w-4" />
                          </Button>
                        </div>
                        {stockResults.map((result) => (
                          <button
                            key={result.symbol}
                            onClick={() => getStockQuote(result.symbol)}
                            className="w-full p-3 text-left hover:bg-[#D4AF37]/10 border-b border-[#D4AF37]/10"
                          >
                            <p className="text-[#FAFAFA] font-medium">{result.symbol}</p>
                            <p className="text-xs text-[#FAFAFA]/50 truncate">{result.name}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {marketTab === 'forex' && (
                      <div className="p-4 space-y-4">
                        <Select value={forexFrom} onValueChange={setForexFrom}>
                          <SelectTrigger className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1c1814] border-[#D4AF37]/30">
                            {['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD'].map(c => (
                              <SelectItem key={c} value={c} className="text-[#FAFAFA]">{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="text-center text-[#D4AF37]">↓</div>
                        <Select value={forexTo} onValueChange={setForexTo}>
                          <SelectTrigger className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1c1814] border-[#D4AF37]/30">
                            {['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD'].map(c => (
                              <SelectItem key={c} value={c} className="text-[#FAFAFA]">{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button onClick={getForexRate} className="w-full btn-gold">Get Rate</Button>
                        {forexRate && (
                          <div className="p-4 bg-[#1c1814] border border-[#D4AF37]/20 text-center">
                            <p className="text-[#FAFAFA]/60 text-sm">1 {forexRate.from} =</p>
                            <p className="font-display text-3xl text-[#D4AF37]">{forexRate.rate?.toFixed(4)} {forexRate.to}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
            
            {/* Center - Chart & Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="card-finance" data-testid="chart-card">
                <CardHeader className="border-b border-[#D4AF37]/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {selectedAsset?.image && (
                        <img src={selectedAsset.image} alt="" className="w-10 h-10" />
                      )}
                      <div>
                        <CardTitle className="font-display text-xl text-[#FAFAFA]">
                          {selectedAsset?.name || selectedAsset?.symbol || 'Select Asset'}
                        </CardTitle>
                        <p className="text-sm text-[#FAFAFA]/50">{selectedAsset?.symbol?.toUpperCase()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-2xl text-[#D4AF37]">
                        {formatCurrency(selectedAsset?.current_price || selectedAsset?.price)}
                      </p>
                      {selectedAsset?.price_change_percentage_24h && (
                        <p className={`text-sm flex items-center justify-end ${
                          selectedAsset.price_change_percentage_24h >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {selectedAsset.price_change_percentage_24h >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                          {formatPercent(selectedAsset.price_change_percentage_24h)} (24h)
                        </p>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div ref={chartContainerRef} className="w-full" data-testid="price-chart" />
                </CardContent>
              </Card>
              
              {selectedAsset?.market_cap && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="card-finance">
                    <CardContent className="p-4">
                      <p className="text-xs text-[#FAFAFA]/50 mb-1">Market Cap</p>
                      <p className="font-display text-lg text-[#FAFAFA]">
                        ${(selectedAsset.market_cap / 1e9).toFixed(2)}B
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="card-finance">
                    <CardContent className="p-4">
                      <p className="text-xs text-[#FAFAFA]/50 mb-1">24h Volume</p>
                      <p className="font-display text-lg text-[#FAFAFA]">
                        ${(selectedAsset.total_volume / 1e9).toFixed(2)}B
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="card-finance">
                    <CardContent className="p-4">
                      <p className="text-xs text-[#FAFAFA]/50 mb-1">24h High</p>
                      <p className="font-display text-lg text-emerald-400">
                        {formatCurrency(selectedAsset.high_24h)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="card-finance">
                    <CardContent className="p-4">
                      <p className="text-xs text-[#FAFAFA]/50 mb-1">24h Low</p>
                      <p className="font-display text-lg text-red-400">
                        {formatCurrency(selectedAsset.low_24h)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
            
            {/* Right - Trade Panel */}
            <div className="lg:col-span-1 space-y-6">
              {/* Alpaca Account Card - Show when connected */}
              {alpacaStatus.connected && alpacaAccount && (
                <Card className="card-finance border-emerald-500/30" data-testid="alpaca-account">
                  <CardHeader className="border-b border-emerald-500/20 pb-4">
                    <CardTitle className="font-display text-lg text-[#FAFAFA] flex items-center gap-2">
                      <Zap className="h-5 w-5 text-emerald-400" />
                      Alpaca {alpacaAccount.paper ? 'Paper' : 'Live'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="text-center mb-4">
                      <p className="text-xs text-[#FAFAFA]/50 mb-1">Portfolio Value</p>
                      <p className="font-display text-2xl text-emerald-400">
                        {formatCurrency(alpacaAccount.portfolio_value)}
                      </p>
                      {alpacaAccount.last_equity && (
                        <p className={`text-sm ${alpacaAccount.equity - alpacaAccount.last_equity >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(alpacaAccount.equity - alpacaAccount.last_equity)} today
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#FAFAFA]/50">Buying Power</span>
                        <span className="text-[#FAFAFA]">{formatCurrency(alpacaAccount.buying_power)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#FAFAFA]/50">Cash</span>
                        <span className="text-[#FAFAFA]">{formatCurrency(alpacaAccount.cash)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#FAFAFA]/50">Positions</span>
                        <span className="text-[#FAFAFA]">{alpacaPositions.length} stocks</span>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-4 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      onClick={() => { fetchAlpacaAccount(); fetchAlpacaPositions(); }}
                    >
                      <RefreshCw className="h-3 w-3 mr-2" />
                      Refresh
                    </Button>
                  </CardContent>
                </Card>
              )}
              
              {/* Paper Trading Portfolio - Show when not connected */}
              {!alpacaStatus.connected && (
                <Card className="card-finance" data-testid="portfolio-summary">
                  <CardHeader className="border-b border-[#D4AF37]/20 pb-4">
                    <CardTitle className="font-display text-lg text-[#FAFAFA] flex items-center gap-2">
                      <Wallet className="h-5 w-5 text-[#D4AF37]" />
                      Paper Portfolio
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="text-center mb-4">
                      <p className="text-xs text-[#FAFAFA]/50 mb-1">Total Value</p>
                      <p className="font-display text-2xl text-[#D4AF37]">
                        {formatCurrency(portfolioAnalytics?.total_value || portfolio?.cash_balance || 10000)}
                      </p>
                      {portfolioAnalytics && (
                        <p className={`text-sm ${portfolioAnalytics.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(portfolioAnalytics.total_pnl)} ({formatPercent(portfolioAnalytics.pnl_percent)})
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#FAFAFA]/50">Cash</span>
                        <span className="text-[#FAFAFA]">{formatCurrency(portfolio?.cash_balance || 10000)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#FAFAFA]/50">Holdings</span>
                        <span className="text-[#FAFAFA]">{portfolio?.holdings?.length || 0} assets</span>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-4 border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      onClick={() => setBrokerModalOpen(true)}
                    >
                      <Link2 className="h-3 w-3 mr-2" />
                      Connect Real Broker
                    </Button>
                  </CardContent>
                </Card>
              )}
              
              <Card className="card-finance" data-testid="trade-form">
                <CardHeader className="border-b border-[#D4AF37]/20 pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-display text-lg text-[#FAFAFA]">
                      Place Order
                    </CardTitle>
                    {alpacaStatus.connected && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs">
                        Real Trades
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={tradeForm.side === 'buy' ? 'default' : 'outline'}
                      onClick={() => setTradeForm(prev => ({ ...prev, side: 'buy' }))}
                      className={tradeForm.side === 'buy' ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-[#D4AF37]/30 text-[#FAFAFA]'}
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Buy
                    </Button>
                    <Button
                      variant={tradeForm.side === 'sell' ? 'default' : 'outline'}
                      onClick={() => setTradeForm(prev => ({ ...prev, side: 'sell' }))}
                      className={tradeForm.side === 'sell' ? 'bg-red-500 hover:bg-red-600' : 'border-[#D4AF37]/30 text-[#FAFAFA]'}
                    >
                      <TrendingDown className="h-4 w-4 mr-2" />
                      Sell
                    </Button>
                  </div>
                  
                  <div>
                    <label className="text-xs text-[#FAFAFA]/50 mb-1 block">Symbol</label>
                    {alpacaStatus.connected ? (
                      <Input
                        value={tradeForm.symbol}
                        onChange={(e) => setTradeForm(prev => ({ ...prev, symbol: e.target.value.toUpperCase() }))}
                        placeholder="AAPL, TSLA, GOOGL..."
                        className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA] text-center font-bold"
                      />
                    ) : (
                      <Input
                        value={tradeForm.symbol}
                        readOnly
                        className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA] text-center font-bold"
                      />
                    )}
                  </div>
                  
                  {!alpacaStatus.connected && (
                    <div>
                      <label className="text-xs text-[#FAFAFA]/50 mb-1 block">Price</label>
                      <div className="p-3 bg-[#1c1814] border border-[#D4AF37]/20 text-center">
                        <span className="font-display text-xl text-[#D4AF37]">{formatCurrency(tradeForm.price)}</span>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-xs text-[#FAFAFA]/50 mb-1 block">
                      {alpacaStatus.connected ? 'Shares' : 'Quantity'}
                    </label>
                    <Input
                      type="number"
                      value={tradeForm.quantity}
                      onChange={(e) => setTradeForm(prev => ({ ...prev, quantity: e.target.value }))}
                      placeholder="0"
                      className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA]"
                      data-testid="quantity-input"
                    />
                  </div>
                  
                  {!alpacaStatus.connected && (
                    <div className="p-3 bg-[#1c1814] border border-[#D4AF37]/20">
                      <div className="flex justify-between">
                        <span className="text-[#FAFAFA]/50">Total</span>
                        <span className="font-display text-[#D4AF37]">
                          {formatCurrency((parseFloat(tradeForm.quantity) || 0) * tradeForm.price)}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={alpacaStatus.connected ? placeAlpacaOrder : executeTrade}
                    disabled={!tradeForm.quantity || !tradeForm.symbol}
                    className={`w-full py-6 ${
                      tradeForm.side === 'buy' 
                        ? 'bg-emerald-500 hover:bg-emerald-600' 
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                    data-testid="execute-trade-btn"
                  >
                    {tradeForm.side === 'buy' ? 'Buy' : 'Sell'} {tradeForm.symbol}
                  </Button>
                  
                  <p className="text-xs text-center text-[#FAFAFA]/40">
                    {alpacaStatus.connected 
                      ? `${alpacaAccount?.paper ? 'Paper trading' : '⚠️ Live trading'} via Alpaca`
                      : 'Paper trading • Practice mode'
                    }
                  </p>
                </CardContent>
              </Card>
              
              {/* Alpaca Positions - Show when connected */}
              {alpacaStatus.connected && alpacaPositions.length > 0 && (
                <Card className="card-finance border-emerald-500/30">
                  <CardHeader className="border-b border-emerald-500/20 pb-4">
                    <CardTitle className="font-display text-lg text-[#FAFAFA]">
                      Positions ({alpacaPositions.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[200px]">
                      <div className="divide-y divide-emerald-500/10">
                        {alpacaPositions.map((pos) => (
                          <div key={pos.symbol} className="p-3 flex items-center justify-between">
                            <div>
                              <p className="text-[#FAFAFA] font-medium">{pos.symbol}</p>
                              <p className="text-xs text-[#FAFAFA]/50">{pos.qty} shares @ {formatCurrency(pos.avg_entry_price)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[#FAFAFA]">{formatCurrency(pos.market_value)}</p>
                              <p className={`text-xs ${pos.unrealized_pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {formatCurrency(pos.unrealized_pl)} ({pos.unrealized_plpc.toFixed(2)}%)
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
        
        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div data-testid="courses-section">
            {/* Progress Overview */}
            <Card className="card-finance mb-8">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-display text-2xl text-[#FAFAFA]">Trading Education</h2>
                    <p className="text-[#FAFAFA]/60">Master the markets with comprehensive courses</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#FAFAFA]/50 mb-1">Overall Progress</p>
                    <p className="font-display text-3xl text-[#D4AF37]">{getTotalProgress()}%</p>
                  </div>
                </div>
                <Progress value={getTotalProgress()} className="h-3" />
              </CardContent>
            </Card>
            
            <div className="space-y-6">
              {courses.map((course) => {
                const completionPercent = getCourseCompletionPercent(course.id, course.lessons);
                const isExpanded = expandedCourse === course.id;
                
                return (
                  <Card key={course.id} className="card-finance overflow-hidden" data-testid={`course-${course.id}`}>
                    <div 
                      className="flex flex-col md:flex-row cursor-pointer"
                      onClick={() => setExpandedCourse(isExpanded ? null : course.id)}
                    >
                      <div className="md:w-64 h-48 md:h-auto overflow-hidden relative flex-shrink-0">
                        <img 
                          src={course.image} 
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#1c1814]/90 md:block hidden" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1c1814] to-transparent md:hidden" />
                      </div>
                      
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={`${
                                course.level === 'Beginner' ? 'bg-emerald-500' :
                                course.level === 'Intermediate' ? 'bg-blue-500' : 'bg-purple-500'
                              }`}>
                                {course.level}
                              </Badge>
                              <span className="text-[#FAFAFA]/50 text-sm">{course.lessons.length} lessons</span>
                            </div>
                            <h3 className="font-display text-2xl text-[#FAFAFA]">{course.title}</h3>
                            <p className="text-[#FAFAFA]/60 mt-1">{course.description}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-2xl font-display text-[#D4AF37]">{completionPercent}%</p>
                              <p className="text-xs text-[#FAFAFA]/50">Complete</p>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="h-6 w-6 text-[#D4AF37]" />
                            ) : (
                              <ChevronDown className="h-6 w-6 text-[#D4AF37]" />
                            )}
                          </div>
                        </div>
                        <Progress value={completionPercent} className="h-2" />
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="border-t border-[#D4AF37]/20 p-6 bg-[#1c1814]/50">
                        <div className="grid md:grid-cols-2 gap-4">
                          {course.lessons.map((lesson, idx) => {
                            const isCompleted = (courseProgress[course.id] || []).includes(lesson.id);
                            
                            return (
                              <div 
                                key={lesson.id}
                                className={`p-4 border transition-all cursor-pointer ${
                                  isCompleted 
                                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                                    : 'bg-[#252540] border-[#D4AF37]/10 hover:border-[#D4AF37]/30'
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleLessonComplete(course.id, lesson.id);
                                    }}
                                    className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                                      isCompleted 
                                        ? 'bg-emerald-500 text-white' 
                                        : 'border-2 border-[#D4AF37]/30 hover:border-[#D4AF37]'
                                    }`}
                                  >
                                    {isCompleted && <Check className="h-4 w-4" />}
                                  </button>
                                  
                                  <div className="flex-1" onClick={() => openLesson(course, lesson)}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-[#D4AF37] text-sm font-bold">Lesson {idx + 1}</span>
                                      <span className="text-[#FAFAFA]/40 text-xs">• {lesson.duration}</span>
                                    </div>
                                    <h4 className={`font-medium ${isCompleted ? 'text-emerald-400' : 'text-[#FAFAFA]'}`}>
                                      {lesson.title}
                                    </h4>
                                  </div>
                                  
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => openLesson(course, lesson)}
                                    className="text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                  >
                                    <Play className="h-4 w-4 mr-1" />
                                    Read
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
            
            {/* Webinar CTA */}
            <Card className="card-finance mt-8">
              <CardContent className="p-8 text-center">
                <Award className="h-12 w-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-display text-2xl text-[#FAFAFA] mb-2">Live Webinar Sessions</h3>
                <p className="text-[#FAFAFA]/60 mb-4 max-w-xl mx-auto">
                  Join our live webinar sessions where we go through these courses in real-time. 
                  Ask questions, get personalized feedback, and connect with other learners.
                </p>
                <Button 
                  onClick={() => window.open('https://www.nouveaurichelegacy.com/booking-calendar/business-consultation', '_blank')}
                  className="btn-gold"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Book a Webinar Session
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <div data-testid="portfolio-section" className="space-y-6">
            {/* Portfolio Overview */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="card-finance">
                <CardContent className="p-6">
                  <p className="text-xs text-[#FAFAFA]/50 mb-1">Total Value</p>
                  <p className="font-display text-2xl text-[#D4AF37]">
                    {formatCurrency(portfolioAnalytics?.total_value || 10000)}
                  </p>
                </CardContent>
              </Card>
              <Card className="card-finance">
                <CardContent className="p-6">
                  <p className="text-xs text-[#FAFAFA]/50 mb-1">Cash Balance</p>
                  <p className="font-display text-2xl text-[#FAFAFA]">
                    {formatCurrency(portfolioAnalytics?.cash_balance || 10000)}
                  </p>
                </CardContent>
              </Card>
              <Card className="card-finance">
                <CardContent className="p-6">
                  <p className="text-xs text-[#FAFAFA]/50 mb-1">P&L</p>
                  <p className={`font-display text-2xl ${(portfolioAnalytics?.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(portfolioAnalytics?.total_pnl || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="card-finance">
                <CardContent className="p-6">
                  <p className="text-xs text-[#FAFAFA]/50 mb-1">Return</p>
                  <p className={`font-display text-2xl ${(portfolioAnalytics?.pnl_percent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatPercent(portfolioAnalytics?.pnl_percent || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Holdings */}
            <Card className="card-finance">
              <CardHeader className="border-b border-[#D4AF37]/20">
                <CardTitle className="font-display text-lg text-[#FAFAFA]">Your Holdings</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {portfolioAnalytics?.holdings?.length > 0 ? (
                  <div className="divide-y divide-[#D4AF37]/10">
                    {portfolioAnalytics.holdings.map((holding, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-[#D4AF37]/10 rounded">
                            <DollarSign className="h-6 w-6 text-[#D4AF37]" />
                          </div>
                          <div>
                            <p className="text-[#FAFAFA] font-medium">{holding.symbol}</p>
                            <p className="text-sm text-[#FAFAFA]/50">{holding.asset_type}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#FAFAFA]">{holding.quantity.toFixed(4)} units</p>
                          <p className="text-sm text-[#FAFAFA]/50">Avg: {formatCurrency(holding.avg_price)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[#D4AF37] font-display">
                            {formatCurrency(holding.quantity * holding.avg_price)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <PieChart className="h-12 w-12 text-[#FAFAFA]/20 mx-auto mb-4" />
                    <p className="text-[#FAFAFA]/60">No holdings yet. Start trading to build your portfolio.</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Recent Trades */}
            <Card className="card-finance">
              <CardHeader className="border-b border-[#D4AF37]/20">
                <CardTitle className="font-display text-lg text-[#FAFAFA]">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {portfolioAnalytics?.recent_trades?.length > 0 ? (
                  <div className="divide-y divide-[#D4AF37]/10">
                    {portfolioAnalytics.recent_trades.slice(0, 5).map((trade, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded ${trade.side === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                            {trade.side === 'buy' ? (
                              <TrendingUp className="h-5 w-5 text-emerald-400" />
                            ) : (
                              <TrendingDown className="h-5 w-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-[#FAFAFA] font-medium">
                              {trade.side === 'buy' ? 'Bought' : 'Sold'} {trade.symbol}
                            </p>
                            <p className="text-sm text-[#FAFAFA]/50">
                              {new Date(trade.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#FAFAFA]">{trade.quantity} @ {formatCurrency(trade.price)}</p>
                          <p className={trade.side === 'buy' ? 'text-red-400' : 'text-emerald-400'}>
                            {trade.side === 'buy' ? '-' : '+'}{formatCurrency(trade.total)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-[#FAFAFA]/60">No recent trades</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* History Tab */}
        {activeTab === 'history' && (
          <div data-testid="history-section">
            <div className="mb-8">
              <h2 className="font-display text-2xl text-[#FAFAFA] mb-2">Trade History</h2>
              <p className="text-[#FAFAFA]/60">Your complete trading activity</p>
            </div>
            
            <Card className="card-finance">
              <CardContent className="p-0">
                {trades.length > 0 ? (
                  <div className="divide-y divide-[#D4AF37]/10">
                    {trades.map((trade, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded ${trade.side === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                            {trade.side === 'buy' ? (
                              <TrendingUp className="h-5 w-5 text-emerald-400" />
                            ) : (
                              <TrendingDown className="h-5 w-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-[#FAFAFA] font-medium">
                              {trade.side === 'buy' ? 'Bought' : 'Sold'} {trade.symbol}
                            </p>
                            <p className="text-sm text-[#FAFAFA]/50">
                              {new Date(trade.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#FAFAFA]">{trade.quantity} @ {formatCurrency(trade.price)}</p>
                          <p className={trade.side === 'buy' ? 'text-red-400' : 'text-emerald-400'}>
                            {trade.side === 'buy' ? '-' : '+'}{formatCurrency(trade.total)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Clock className="h-12 w-12 text-[#FAFAFA]/20 mx-auto mb-4" />
                    <p className="text-[#FAFAFA]/60">No trades yet. Start trading to see your history here.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Lesson Modal */}
      <Dialog open={lessonModalOpen} onOpenChange={setLessonModalOpen}>
        <DialogContent className="bg-[#1c1814] border-[#D4AF37]/20 max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader className="border-b border-[#D4AF37]/20 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#D4AF37] text-sm mb-1">{selectedLesson?.course?.title}</p>
                <DialogTitle className="font-display text-2xl text-[#FAFAFA]">
                  {selectedLesson?.lesson?.title}
                </DialogTitle>
                <DialogDescription className="text-[#FAFAFA]/50">
                  {selectedLesson?.lesson?.duration}
                </DialogDescription>
              </div>
              <Button
                onClick={() => {
                  if (selectedLesson) {
                    toggleLessonComplete(selectedLesson.course.id, selectedLesson.lesson.id);
                  }
                }}
                className={`${
                  selectedLesson && (courseProgress[selectedLesson.course.id] || []).includes(selectedLesson.lesson.id)
                    ? 'bg-emerald-500 hover:bg-emerald-600'
                    : 'btn-gold'
                }`}
              >
                <Check className="h-4 w-4 mr-2" />
                {selectedLesson && (courseProgress[selectedLesson.course.id] || []).includes(selectedLesson.lesson.id)
                  ? 'Completed'
                  : 'Mark Complete'
                }
              </Button>
            </div>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="prose prose-invert prose-gold max-w-none py-4">
              {selectedLesson?.lesson?.content.split('\n').map((line, idx) => {
                if (line.startsWith('# ')) {
                  return <h1 key={idx} className="text-[#D4AF37] font-display text-3xl mb-4">{line.slice(2)}</h1>;
                } else if (line.startsWith('## ')) {
                  return <h2 key={idx} className="text-[#FAFAFA] font-display text-xl mt-6 mb-3">{line.slice(3)}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={idx} className="text-[#D4AF37] font-display text-lg mt-4 mb-2">{line.slice(4)}</h3>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={idx} className="text-[#FAFAFA] font-bold my-2">{line.slice(2, -2)}</p>;
                } else if (line.startsWith('- ')) {
                  return <li key={idx} className="text-[#FAFAFA]/80 ml-4">{line.slice(2)}</li>;
                } else if (line.trim() === '') {
                  return <br key={idx} />;
                } else {
                  return <p key={idx} className="text-[#FAFAFA]/80 my-2 leading-relaxed">{line}</p>;
                }
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      
      {/* Broker Connection Modal */}
      <Dialog open={brokerModalOpen} onOpenChange={setBrokerModalOpen}>
        <DialogContent className="bg-[#1c1814] border-[#D4AF37]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#FAFAFA] flex items-center gap-2">
              <Link2 className="h-6 w-6 text-[#D4AF37]" />
              Connect Alpaca
            </DialogTitle>
            <DialogDescription className="text-[#FAFAFA]/60">
              Connect your Alpaca account to trade real stocks. Paper trading is free!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Info Banner */}
            <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg">
              <p className="text-sm text-[#FAFAFA]/80">
                <strong className="text-[#D4AF37]">New to Alpaca?</strong> Create a free account at{' '}
                <a 
                  href="https://alpaca.markets" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] underline hover:text-[#D4AF37]/80"
                >
                  alpaca.markets
                </a>
                {' '}to get your API keys.
              </p>
            </div>
            
            {/* API Key Input */}
            <div className="space-y-2">
              <Label className="text-[#FAFAFA]">API Key</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#FAFAFA]/40" />
                <Input
                  type="text"
                  value={alpacaCredentials.api_key}
                  onChange={(e) => setAlpacaCredentials(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="PK..."
                  className="bg-[#0d0c0a] border-[#D4AF37]/30 text-[#FAFAFA] pl-10"
                />
              </div>
            </div>
            
            {/* Secret Key Input */}
            <div className="space-y-2">
              <Label className="text-[#FAFAFA]">Secret Key</Label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#FAFAFA]/40" />
                <Input
                  type="password"
                  value={alpacaCredentials.secret_key}
                  onChange={(e) => setAlpacaCredentials(prev => ({ ...prev, secret_key: e.target.value }))}
                  placeholder="••••••••••••••••"
                  className="bg-[#0d0c0a] border-[#D4AF37]/30 text-[#FAFAFA] pl-10"
                />
              </div>
            </div>
            
            {/* Paper Trading Toggle */}
            <div className="flex items-center justify-between p-4 bg-[#0d0c0a] border border-[#D4AF37]/20 rounded-lg">
              <div className="space-y-1">
                <Label className="text-[#FAFAFA]">Paper Trading</Label>
                <p className="text-xs text-[#FAFAFA]/50">
                  Practice with virtual money (recommended for beginners)
                </p>
              </div>
              <Switch
                checked={alpacaCredentials.paper}
                onCheckedChange={(checked) => setAlpacaCredentials(prev => ({ ...prev, paper: checked }))}
              />
            </div>
            
            {!alpacaCredentials.paper && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">
                  <strong>⚠️ Warning:</strong> Live trading uses real money. Make sure you understand the risks.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setBrokerModalOpen(false)}
              className="border-[#D4AF37]/30 text-[#FAFAFA]"
            >
              Cancel
            </Button>
            <Button
              onClick={connectAlpaca}
              disabled={connectingBroker || !alpacaCredentials.api_key || !alpacaCredentials.secret_key}
              className="bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90"
            >
              {connectingBroker ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Connect
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
